import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import { useLoad } from '@/features/administration/hooks'
import { EMPTY_UNIT_FILTERS, listUnits } from '@/features/geography/api'
import type { CandidateDto } from '@/shared/api/types'
import { CODE_LIST_UNAVAILABLE, usePoliticsCodes } from '@/features/polling/useVocabulary'
import { createCandidate, listCoalitions, listParties, listSeatTypes, updateCandidate } from './api'
import {
  EntityChip, candidateStatusLabel, seatScopeSentence, seatTypeLabel,
} from './vocab'

/** `UnitQueryService.list` refuses anything outside 1..200, and 200 is a level's worth. */
const UNIT_PAGE = 200
const LOOKUP_PAGE = 200

/**
 * Register a candidate.
 *
 * ## The seat type decides which units are legal, and the SERVER owns that rule
 *
 * `GET /politics/seat-types` publishes `{code, requiredUnitLevel}` — PRESIDENT is
 * national, GOVERNOR/SENATOR/WOMAN_REP county, MP constituency, MCA ward. This
 * form reads that mapping and does two things with it: it filters the unit picker
 * to that level, and it says the rule in words beside the picker ("A GOVERNOR
 * contests a COUNTY"). It does not re-implement the rule. If the two ever
 * disagree, the server's 400 `politics/seat-scope` is rendered verbatim and the
 * server wins visibly — a client that silently blocked a legal combination would
 * be indistinguishable from a broken form.
 *
 * Changing the seat type after a unit is chosen CLEARS the unit rather than
 * carrying a now-illegal one into the submit. It says so when it happens; a field
 * that empties itself without explanation reads as a bug.
 *
 * ## Empty party means independent, in words
 *
 * `partyId: null` is the wire representation of an independent candidate. A blank
 * select reads as "not filled in yet", so the option is spelled out and the form
 * repeats what it means underneath.
 */
export function CandidateFormDialog({ open, candidate, onClose, onSaved }: {
  open: boolean
  /** `null` creates. */
  candidate: CandidateDto | null
  onClose: () => void
  onSaved: (saved: CandidateDto) => void
}) {
  const editing = candidate !== null

  const [fullName, setFullName] = useState('')
  const [seatTypeCode, setSeatTypeCode] = useState('')
  const [unitId, setUnitId] = useState('')
  const [partyId, setPartyId] = useState('')
  const [coalitionId, setCoalitionId] = useState('')
  const statuses = usePoliticsCodes('candidateStatus')
  const [statusCode, setStatusCode] = useState<string>('PROSPECTIVE')
  const [incumbent, setIncumbent] = useState(false)
  const [notes, setNotes] = useState('')

  /** The unit as the server described it on the record being edited. */
  const [knownUnit, setKnownUnit] = useState<{ id: string; label: string } | null>(null)
  const [unitCleared, setUnitCleared] = useState(false)
  const [unitQuery, setUnitQuery] = useState('')
  const [unitTerm, setUnitTerm] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const seatTypes = useLoad(() => listSeatTypes(), [])
  const parties = useLoad(() => listParties({}, 0, LOOKUP_PAGE), [])
  const coalitions = useLoad(() => listCoalitions({}, 0, LOOKUP_PAGE), [])

  const seat = (seatTypes.data ?? []).find((s) => s.code === seatTypeCode) ?? null
  const requiredLevel = seat?.requiredUnitLevel ?? null

  // One request per pause, not per keystroke: this runs against the geography
  // register over a field connection.
  useEffect(() => {
    const h = setTimeout(() => setUnitTerm(unitQuery.trim()), 300)
    return () => clearTimeout(h)
  }, [unitQuery])

  const units = useLoad(
    () => (requiredLevel
      ? listUnits({ ...EMPTY_UNIT_FILTERS, type: [requiredLevel], q: unitTerm }, 0, UNIT_PAGE)
      : Promise.resolve(null)),
    [requiredLevel, unitTerm])

  useEffect(() => {
    if (!open) return
    setFullName(candidate?.fullName ?? '')
    setSeatTypeCode(candidate?.seatTypeCode ?? '')
    setUnitId(candidate?.unitId ?? '')
    setKnownUnit(candidate
      ? {
          id: candidate.unitId,
          label: candidate.unitLabels.length > 0
            ? candidate.unitLabels.join(' › ')
            : (candidate.unitName ?? candidate.unitId),
        }
      : null)
    setPartyId(candidate?.party?.id ?? '')
    // Only a DIRECT coalition is editable. A derived one belongs to the party and
    // pre-filling it here would silently convert an inherited alignment into a
    // recorded one on the next save.
    setCoalitionId(candidate && !candidate.coalitionDerived ? (candidate.coalition?.id ?? '') : '')
    setStatusCode(candidate?.statusCode ?? 'PROSPECTIVE')
    setIncumbent(candidate?.incumbent ?? false)
    setNotes(candidate?.notes ?? '')
    setUnitQuery('')
    setUnitTerm('')
    setUnitCleared(false)
    setError(null)
    setFieldErrors({})
  }, [open, candidate])

  // The level of whatever is currently selected, as far as this form can know it:
  // from the fetched option when the unit is in the list, otherwise from the
  // record being edited. Null means "cannot tell" — and a level nobody can tell
  // is never used to clear a field.
  const chosenOption = (units.data?.content ?? []).find((u) => u.id === unitId) ?? null
  const chosenLevel: string | null = chosenOption?.unitType
    ?? (candidate && candidate.unitId === unitId ? candidate.unitTypeCode : null)

  function pickSeatType(next: string) {
    setSeatTypeCode(next)
    const nextLevel = (seatTypes.data ?? []).find((s) => s.code === next)?.requiredUnitLevel ?? null
    if (unitId && nextLevel && chosenLevel && nextLevel !== chosenLevel) {
      setUnitId('')
      setKnownUnit(null)
      setUnitCleared(true)
    }
  }

  async function submit() {
    setBusy(true)
    setError(null)
    setFieldErrors({})
    const body = {
      fullName: fullName.trim(),
      seatTypeCode,
      unitId,
      partyId: partyId || null,
      coalitionId: coalitionId || null,
      statusCode,
      incumbent,
      notes: notes.trim() || null,
      // Not edited here — there is no upload flow in this feature — but a PUT
      // replaces the whole record, so omitting it would silently blank the photo
      // of every candidate anybody edits.
      photoFileId: candidate?.photoFileId ?? null,
    }
    try {
      const saved = editing
        ? await updateCandidate(candidate.id, body)
        : await createCandidate(body)
      onSaved(saved)
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors())
      // `politics/seat-scope` names the seat, the level it needs and the level the
      // unit actually is. That is the whole diagnosis, so it is shown as sent.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const unitRows = units.data?.content ?? []
  const ready = fullName.trim().length > 0 && seatTypeCode !== '' && unitId !== ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${candidate.fullName}` : 'New candidate'}
      description="A candidate is a person, a seat and a unit. The party is optional; the unit is not."
      width="lg"
      footer={(
        <>
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={!ready || busy} onClick={submit}>
            {busy ? 'Saving…' : editing ? 'Save' : 'Register'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <ErrorStrip message={seatTypes.error} />

        <Field label="Full name" required error={fieldErrors.fullName}>
          <input className="erp-input w-full" value={fullName} maxLength={200}
            onChange={(e) => setFullName(e.target.value)} placeholder="Johnson Sakaja" />
        </Field>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="Seat" required error={fieldErrors.seatTypeCode}
              hint="Read from /politics/seat-types — the same list the server validates against.">
              <select className="erp-input w-full" value={seatTypeCode}
                onChange={(e) => pickSeatType(e.target.value)}>
                <option value="">
                  {seatTypes.loading ? 'Loading seat types…' : 'Choose a seat…'}
                </option>
                {(seatTypes.data ?? []).map((s) => (
                  <option key={s.code} value={s.code}>{seatTypeLabel(s.code)}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-6">
            <Field label="Status"
              error={fieldErrors.statusCode
                ?? (statuses.unavailable ? CODE_LIST_UNAVAILABLE : undefined)}
              hint="Served in nomination order, which is the order a candidacy travels in.">
              <select className="erp-input w-full" value={statusCode}
                disabled={statuses.loading || statuses.unavailable}
                onChange={(e) => setStatusCode(e.target.value)}>
                {statuses.loading && <option value={statusCode}>Loading…</option>}
                {statuses.codes.map((s) => (
                  <option key={s} value={s}>{candidateStatusLabel(s)}</option>
                ))}
                {statusCode !== '' && !statuses.codes.includes(statusCode) && (
                  <option value={statusCode}>{candidateStatusLabel(statusCode)}</option>
                )}
              </select>
            </Field>
          </div>
        </div>

        <Field
          label="Unit"
          required
          error={fieldErrors.unitId}
          hint={seatTypeCode
            ? `${seatScopeSentence(seatTypeCode, requiredLevel)} Only units at that level are offered.`
            : 'Choose a seat first — the seat decides which level of unit is legal.'}
        >
          <div className="space-y-2">
            <input
              className="erp-input w-full"
              value={unitQuery}
              disabled={!requiredLevel}
              onChange={(e) => setUnitQuery(e.target.value)}
              placeholder={requiredLevel ? `Search ${requiredLevel.toLowerCase()}s…` : 'Pick a seat first'}
              aria-label="Search units"
            />
            <select
              className="erp-input w-full"
              value={unitId}
              // Disabled only while there is no level to filter by. An empty
              // result stays enabled and says why — a dead control with no
              // explanation is unactionable.
              disabled={!requiredLevel}
              onChange={(e) => { setUnitId(e.target.value); setUnitCleared(false) }}
              aria-label="Unit"
            >
              <option value="">
                {!requiredLevel ? 'No seat chosen'
                  : units.loading ? 'Loading units…'
                  : unitRows.length === 0 ? `No ${requiredLevel} matches that search`
                  : `Choose a ${requiredLevel.toLowerCase()}…`}
              </option>
              {/* The record's own unit, kept selectable even when the current
                  search does not return it — otherwise opening an edit dialog and
                  typing in the search box silently discards the unit. */}
              {knownUnit && !unitRows.some((u) => u.id === knownUnit.id) && (
                <option value={knownUnit.id}>{knownUnit.label}</option>
              )}
              {unitRows.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.parentName ? ` — ${u.parentName}` : ''}
                </option>
              ))}
            </select>
          </div>
        </Field>

        {units.error && <Note tone="bad">The unit list could not be loaded: {units.error}</Note>}

        {unitCleared && (
          <Note tone="info" onDismiss={() => setUnitCleared(false)}>
            The unit was cleared because the seat changed: {seatScopeSentence(seatTypeCode, requiredLevel)}
          </Note>
        )}

        {(units.data?.totalElements ?? 0) > unitRows.length && (
          <Note tone="info">
            Showing the first {unitRows.length} of {units.data?.totalElements}{' '}
            {requiredLevel?.toLowerCase()} units. Narrow the search to reach the rest.
          </Note>
        )}

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="Party" error={fieldErrors.partyId}
              hint="Empty means the candidate stands as an independent.">
              <select className="erp-input w-full" value={partyId}
                onChange={(e) => setPartyId(e.target.value)}>
                <option value="">Independent — no party</option>
                {(parties.data?.content ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-6">
            <Field label="Coalition, directly" error={fieldErrors.coalitionId}
              hint="Leave empty to inherit whatever coalition the party is in. Set it only for somebody who sits in the alliance themselves.">
              <select className="erp-input w-full" value={coalitionId}
                onChange={(e) => setCoalitionId(e.target.value)}>
                <option value="">
                  {partyId ? 'Inherit from the party' : 'None'}
                </option>
                {(coalitions.data?.content ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        {!partyId && (
          <Note tone="info">
            No party selected, so this candidate is recorded as an{' '}
            <strong>independent</strong>. They will show as independent everywhere and take a
            coalition only if one is set above.
          </Note>
        )}

        {partyId && coalitionId && (
          <Note tone="info">
            A party and a coalition are both set. The coalition is then recorded DIRECTLY on the
            candidate rather than derived from{' '}
            {(parties.data?.content ?? []).find((p) => p.id === partyId)?.name ?? 'the party'} —
            it will not follow the party if the party leaves.
          </Note>
        )}

        <label className="flex items-center gap-2 text-erp-sm">
          <input type="checkbox" checked={incumbent}
            onChange={(e) => setIncumbent(e.target.checked)} />
          <span>Incumbent — currently holds this seat</span>
        </label>

        <Field label="Notes" error={fieldErrors.notes}>
          <textarea className="erp-input w-full min-h-[72px] py-2" value={notes} maxLength={4000}
            onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {editing && (
          <div className="flex flex-wrap items-center gap-2 text-erp-xs text-muted-foreground">
            <span>Currently:</span>
            {candidate.party
              ? <EntityChip entity={{
                  code: candidate.party.code, name: candidate.party.name,
                  shortName: candidate.party.shortName, colourHex: candidate.party.colourHex,
                }} />
              : <span>independent</span>}
            {candidate.coalition && (
              <EntityChip
                entity={candidate.coalition}
                sub={candidate.coalitionDerived ? 'via party' : 'direct'}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
